import { Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CropField } from '@next/arable-lib/crop-field';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, ReplaySubject, switchMap, take, takeUntil, tap } from 'rxjs';
import { selectCropVarietiesByCropId } from 'src/app/advices/store/selectors';

import { SiteState } from 'src/app/shared/models';
import { UnsavedChangesService } from 'src/app/shared/services/unsaved-changes.service';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import { selectCropYear } from 'src/app/store/selectors';
import {
  GeneralQuestions,
  GenericGeneralQuestionsRequest,
  ProcessedAnswersWithForm,
} from 'src/app/tga/shared/models/models';
import { fetchGenericGeneralQuestions, postGenericGeneralQuestions } from 'src/app/tga/store/tga.actions';
import { selectGenericGeneralQuestions } from 'src/app/tga/store/tga.selectors';

import { GenericGeneralQuestionsService } from './generic-general-questions.service';
import { PotatoType, TgaInputType, TGAQuestion } from '../../../models/question-models';
import { BaseGenericQuestionnaireComponent } from '../base-generic-questionnaire/base-generic-questionnaire.component';

@Component({
  selector: 'app-generic-general-questionnaire',
  templateUrl: './generic-general-questionnaire.component.html',
  styleUrl: './generic-general-questionnaire.component.scss',
  imports: [BaseGenericQuestionnaireComponent, TranslatePipe],
})
export class GenericGeneralQuestionnaireComponent implements OnInit, OnDestroy {
  protected readonly isCropYearReadOnly = inject(CropYearService).isReadOnly;
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly genericGeneralQuestionsService = inject(GenericGeneralQuestionsService);
  private readonly route = inject(ActivatedRoute);
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);

  cropYear: number | null = null;
  cropField: CropField | undefined = undefined;
  formGroup: FormGroup = new FormGroup({});
  generalQuestions: TGAQuestion<TgaInputType>[] = [];
  formReady = signal(false);
  private previousFormValues: Record<string, unknown> = {};
  private hasShownConfirmation = false;

  destroyed$ = new ReplaySubject<boolean>(1);
  readonly cropVarieties$ = this.store.select(selectCropVarietiesByCropId);
  readonly selectGenericGeneralQuestions$ = this.store.select(selectGenericGeneralQuestions);
  private readonly selectedCropYear$ = this.store.select(selectCropYear);

  readonly params$ = this.createParamsStream();
  readonly questions$ = this.createQuestionsStream();

  ngOnInit(): void {
    this.initializeParamsAndQuestions();
    this.initializeQuestionsAndForm();
  }

  answersUpdated(data: ProcessedAnswersWithForm): void {
    if (this.isCropYearReadOnly()) return;

    this.selectGenericGeneralQuestions$.pipe(take(1)).subscribe((questionsWithStatus) => {
      const shouldShowDialog = questionsWithStatus?.hasFilledInSpecific === true && !this.hasShownConfirmation;

      if (shouldShowDialog) {
        this.dialogService
          .confirm({
            title: this.locale.translate('TGA.Questionnaire.Generic.OverwriteConfirmMessage'),
            text: this.locale.translate('TGA.Questionnaire.Generic.OverwriteConfirmDescription'),
            pushHistoryState: false,
          })
          .subscribe((result) => {
            if (this.isCropYearReadOnly()) return;

            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.hasShownConfirmation = true;
              if (this.formGroup.valid) {
                this.validateAndSubmit(data);
              }
            } else {
              // User cancelled - revert form to previous values
              this.revertFormValues(data.formGroup);
            }
          });
      } else {
        this.validateAndSubmit(data);
      }
    });
  }

  private validateAndSubmit(data: ProcessedAnswersWithForm) {
    if (this.isCropYearReadOnly()) return;

    const { answers: processedAnswers, formGroup } = data;

    this.markFormGroupTouched(formGroup);

    if (formGroup.valid) {
      this.submitAnswers(processedAnswers);
    }
  }

  private submitAnswers(processedAnswers: Record<string, unknown>): void {
    if (this.isCropYearReadOnly()) return;

    if (this.cropYear) {
      const generalQuestions: GeneralQuestions = {
        targetPurpose: processedAnswers['potatoType'] as PotatoType,
        targetExport: processedAnswers['export'] === 'yes',
      };
      const answersPayload: GenericGeneralQuestionsRequest = {
        ...generalQuestions,
        cropYear: new Date().getFullYear(),
      };

      this.store.dispatch(
        postGenericGeneralQuestions({
          answers: answersPayload,
        }),
      );

      this.unsavedChangesService.markFormCompleted();
    }
  }

  private revertFormValues(formGroup: FormGroup): void {
    formGroup.patchValue(this.previousFormValues, { emitEvent: false });
    this.hasShownConfirmation = false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private createParamsStream() {
    return this.route.params.pipe(
      takeUntil(this.destroyed$),
      map(this.extractRouteParams.bind(this)),
      filter(this.validateRouteParams),
    );
  }

  private extractRouteParams(params: { [key: string]: string | undefined }) {
    this.cropYear = Number.parseInt(params['cropYear'] || new Date().getFullYear().toString(), 10);
    return {
      cropYear: this.cropYear,
    };
  }

  private validateRouteParams(params: { cropYear: number }) {
    return !Number.isNaN(params.cropYear);
  }

  private createQuestionsStream() {
    return combineLatest([this.selectGenericGeneralQuestions$]).pipe(
      tap(() => {
        this.genericGeneralQuestionsService.initializeGeneralQuestions();
      }),
      map(() => {
        return this.genericGeneralQuestionsService.initValues();
      }),
    );
  }

  private initializeParamsAndQuestions() {
    this.params$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((params) =>
          this.questions$.pipe(
            take(1),
            tap((questions) => this.checkAndFetchMissingQuestions(questions)),
            map(() => params),
          ),
        ),
      )
      .subscribe((params) => {
        this.cropYear = params.cropYear;
      });
  }

  private initializeQuestionsAndForm() {
    this.questions$
      .pipe(
        takeUntil(this.destroyed$),
        filter((questions) => questions.length > 0),
      )
      .subscribe((questions) => {
        this.generalQuestions = questions;
        this.buildForm();
      });
  }

  private checkAndFetchMissingQuestions(questions: TGAQuestion<TgaInputType>[]) {
    this.formReady.set(false);
    const hasMissingValues = questions.some((q) => q.value === null || q.value === undefined);

    if (hasMissingValues) {
      this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
        this.store.dispatch(fetchGenericGeneralQuestions({ cropYear }));
      });
    }
  }

  private buildForm(): void {
    if (!this.generalQuestions || this.generalQuestions.length === 0) {
      return;
    }

    const group: { [key: string]: AbstractControl } = {};

    for (const question of this.generalQuestions) {
      let initialValue: TgaInputType | Date | undefined = question.value;

      // Convert year number to Date object for date-year questions
      if (question.controlType === 'date-year' && typeof question.value === 'number') {
        initialValue = new Date(question.value, 0, 1);
      }

      const control = new FormControl(initialValue);
      this.addQuestionValidators(question, control);
      group[question.key] = control;
    }
    this.formGroup = new FormGroup(group);

    this.unsavedChangesService.registerForm(this.formGroup);

    // Store initial values as previous values
    this.previousFormValues = { ...this.formGroup.value };

    this.formReady.set(true);
  }
  private addQuestionValidators(question: TGAQuestion<TgaInputType>, control: AbstractControl): void {
    if (question.required) {
      control.addValidators(Validators.required);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();

        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      }
    }
  }
}
